'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import type { AxfoodProductDetailsViewModel } from '@occ/api-client';
import useCart from '@hooks/useCart';
import useStore from '@hooks/useStore';
import useCartActions from '@hooks/useCartActions';
import useProductImpressionTracking from '@hooks/useProductImpressionsTracking';
import {
  trackChangeQuantity,
  trackPercentageOfProductsAdded,
} from '@helpers/analyticsHelpers/trackBuyAllProductsComponent';
import { StyledContainer, StyledFooter, StyledHeading, StyledModal } from './BuyAllProductsModal.styles';
import type { ProductAndQuantity } from '@api/interfaces/componentApi';
import TrackedComponent from '@organisms/TrackedComponent/TrackedComponent';
import BuyAllProductsListItem from '@organisms/BuyAllProductsComponent/BuyAllProductsListItem';
import ScrollShadow from '@components/molecules/ScrollShadow/ScrollShadow';
import Button from '@atoms/Button/Button';

interface Props {
  isOpen: boolean;
  onClose: (purchased?: boolean) => void;
  title: string;
  products: Array<AxfoodProductDetailsViewModel>;
}

const BuyAllProductsModal = ({ isOpen, onClose, title, products }: Props) => {
  const [modalProducts, setModalProducts] = useState<Array<ProductAndQuantity>>([]);
  const [modalAmountOfProducts, setModalAmountOfProducts] = useState(0);

  const t = useTranslations('buyAllProductsComponent');
  const { cart } = useCart();
  const { postToCart } = useCartActions();
  const { store } = useStore();
  const { onProductShown: onProductShownInModal } = useProductImpressionTracking('buy-all-products-component-modal');

  const populateModalProducts = (productList: Array<AxfoodProductDetailsViewModel>) => {
    if (productList) {
      setModalProducts(structuredClone(productList) as Array<ProductAndQuantity>);
    } else {
      setModalProducts([]);
    }
  };

  useEffect(() => {
    if (products) {
      populateModalProducts(products);
      setModalAmountOfProducts(products.length);
    }
  }, [products]);

  const getAmountAlreadyInCart = (productCode: string) => {
    const product = cart?.products.find((item) => item.code === productCode);
    return product?.pickQuantity || 0;
  };

  const handleClose = () => {
    populateModalProducts(products);
    onClose(false);
  };

  const handleConfirm = async () => {
    const cartProducts = modalProducts
      .filter((item: any) => item.quantity > 0)
      .map((item: any) => {
        const amountInCart = getAmountAlreadyInCart(item.code);
        return {
          product: item,
          newQuantity: amountInCart + item.quantity,
          prevQuantity: amountInCart,
          listName: `buy_all_beam | ${title}`,
        };
      });
    await postToCart(cartProducts, store);
    trackPercentageOfProductsAdded((modalAmountOfProducts / products.length).toFixed(1));
    onClose(true);
  };

  if (!isOpen) return null;

  return (
    <StyledModal size="medium" closeOnEscape noPadding hasCloseIcon onClose={handleClose}>
      <StyledContainer>
        <StyledHeading size="small" variant="h2">
          {title}
        </StyledHeading>

        <ScrollShadow>
          {modalProducts.map((product, i) => (
            <TrackedComponent
              index={i}
              product={product}
              key={`buyAll-${product.code}`}
              onInView={() => onProductShownInModal(product, i)}
            >
              <BuyAllProductsListItem
                product={product}
                quantity={product.quantity}
                quantityCallback={(quantity: number) => {
                  product.quantity = quantity;
                  setModalAmountOfProducts(modalProducts.filter((item) => item.quantity > 0).length);
                  trackChangeQuantity(product.quantity < quantity);
                }}
              />
            </TrackedComponent>
          ))}
        </ScrollShadow>

        <StyledFooter>
          <Button fullWidth variant="secondary" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button fullWidth variant="primary" onClick={handleConfirm} disabled={modalAmountOfProducts === 0}>
            {t('buyAmountOfProducts', {
              amount: modalAmountOfProducts,
              total: products.length,
            })}
          </Button>
        </StyledFooter>
      </StyledContainer>
    </StyledModal>
  );
};

export default BuyAllProductsModal;
